import { toUpper } from 'lodash';

import {
  normalizeTurvoItemUnitForForm,
  normalizeTurvoTransportSizeForForm,
  normalizeTurvoTransportTypeForForm,
} from 'pages/QuoteView/LoadBuilding/TurvoSectionForms/utils';
import CheckCallSource from 'types/enums/CheckCallSource';
import { TMS } from 'types/enums/Integrations';
import { titleCase } from 'utils/formatStrings';
import { normalizeToArray } from 'utils/loadInfoAndBuilding';
import { normalizeTimezone } from 'utils/normalizeTimezone';
import { normalizeDatesForTMSForm } from 'utils/parseDatesForTMSForm';

import { Order } from './Order';
import { Maybe, MaybeUndef, Undef } from './UtilityTypes';

export type DateOnlyString = `${number}-${number}-${number}`;
export type LoadDateValue = Date | DateOnlyString;

export type TMSEquipmentType = {
  id: string;
  name: string;
  // Transport types (e.g. "VAN") the backend resolves to this record (Revenova).
  defaultForTransportTypes?: string[];
  // Drumkit class (e.g. "FLATBED", "STRAIGHT TRUCK") for a native catalog code (PLS).
  transportType?: string;
};

export type Load = {
  // Capitalized only because of underlying gorm.Model
  ID: MaybeUndef<number>;
  CreatedAt: string;
  UpdatedAt: string;
  DeletedAt: MaybeUndef<string>;

  serviceID: number;
  tmsID: number;
  freightTrackingID: string;
  drumkitUUID: string;
  isPlaceholder: boolean;
  externalTMSID: string;
  status: string;
  mode: string;
  moreThanTwoStops: boolean;
  poNums: MaybeUndef<string>;
  /** Minimal placeholder from sparse PO/rate email (ENG-6475); not persisted on TMS row. */
  batchLoad?: boolean;
  operator: string;
  additionalReferences: AdditionalReference[];
  rateData: RateData;
  customer: Customer;
  billTo: CompanyCoreInfo;
  pickup: Pickup;
  consignee: Dropoff;
  carrier: LoadCarrier;
  specifications: Specifications;
  notes: Note[];
  commodities: Commodity[];
  declaredValueUSD?: number; // Total shipment value in USD (for GlobalTranz cargoValue)
  stops?: LoadStopsItem[];
  orders?: Order[];
  url?: string;
};

export enum LoadStopsItemTypes {
  Pickup = 'pickup',
  Stop = 'stop',
  Delivery = 'delivery',
  Dropoff = 'dropoff',
}

/**
 * Whether a stop is a delivery. Backends send both `delivery` and `dropoff` (models.DropoffStop)
 * for the same thing, so compare against both rather than one enum value.
 */
export function isDeliveryStopType(stopType: MaybeUndef<string>): boolean {
  return (
    stopType === LoadStopsItemTypes.Delivery ||
    stopType === LoadStopsItemTypes.Dropoff
  );
}

export enum LoadStopTypes {
  Pickup = 'pickup',
  Consignee = 'consignee',
}

export type LoadStopsItemAddress = {
  name: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zip: string;
  timezone: string;
  country: string;
  isVerified: boolean;
};

export type LoadStopsItem = {
  id?: string | number;
  order?: number;
  loadID: number;
  stopType: LoadStopsItemTypes;
  stopNumber: number;
  address: LoadStopsItemAddress;
  externalTMSID: string;
  contact: string;
  phone: string;
  email: string;
  businessHours: string;
  refNumber: string;
  readyTime: string;
  readyEndTime: string;
  mustDeliver: string;
  apptRequired: boolean;
  apptType: string;
  apptStartTime: string;
  apptEndTime: string;
  actualStartTime: string;
  actualEndTime: string;
  expectedStartTime: string;
  apptNum?: string;
  apptNote: string;
  // Circle / TransportPro: POs parsed from special-instructions at backend ingest
  poNumbers?: string[];
  // Circle / TransportPro: sum of PAL/CAS counts from parsed note lines
  palletCount?: number;
  timezone: string;
  additionalReferences: Maybe<AdditionalReference[]>;
  // McLeod Builder only: sent as stop notes on create
  hotComment?: string;
  billingComment?: string;
};

export type CompanyCoreInfo = {
  externalTMSID: string;
  name: string;
  nameAddress?: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zipCode: string;
  country?: string;
  contact: string;
  phone: string;
  email: string;
};

export const formatAddress = (address: CompanyCoreInfo): string => {
  if (!address) {
    return '';
  }

  const parts: string[] = [];
  if (address.addressLine1) parts.push(titleCase(address.addressLine1));
  if (address.addressLine2) parts.push(titleCase(address.addressLine2));
  if (address.city) parts.push(titleCase(address.city));
  if (address.state) parts.push(address.state);
  if (address.zipCode) parts.push(toUpper(address.zipCode));

  return parts.join(', ');
};

export const formatAddressCityStateZip = (address: CompanyCoreInfo): string => {
  if (!address) {
    return '';
  }

  const parts: string[] = [];
  if (address.city) parts.push(titleCase(address.city));
  if (address.state) parts.push(address.state);
  if (address.zipCode) parts.push(toUpper(address.zipCode));

  return parts.join(', ');
};

export type Customer = CompanyCoreInfo & {
  refNumber: string;
  refNumberCandidates?: string[];
  defaultCommodity?: string;
  isTest?: boolean;
};

export type Note = {
  updatedBy: string;
  createdAt: string;
  note: string;
  isException: Maybe<boolean>;
  isOnTime: Maybe<boolean>;
  source: string; // Primarily for Relay, e.g. dispatcher, driver

  // GlobalTranz TMS specific fields
  userName?: string;
  text?: string;
  noteType?: string;
  noteTypeID?: number;
  timeStamp?: string;
  noteId?: number;
  isDeleted?: boolean;
  isResolved?: boolean;
  canDeleteNote?: boolean;
  isAttachment?: boolean;
};

export type LoadCarrier = {
  externalTMSID: string;
  name: string;
  mcNumber: string;
  dotNumber: string;
  phone: string;
  dispatcher: string;
  notes: string;
  sealNumber: string;
  scac: string;
  firstDriverName: string;
  firstDriverPhone: string;
  secondDriverName: string;
  secondDriverPhone: string;
  email: string;
  dispatchCity: string;
  dispatchState: string;
  dispatchSource: CheckCallSource;
  truckNumber: string;
  trailerNumber: string;
  rateConfirmationSent: boolean;
  confirmationSentTime: MaybeUndef<string>;
  confirmationReceivedTime: MaybeUndef<string>;
  dispatchedTime: MaybeUndef<string>;
  expectedPickupTime: MaybeUndef<string>;
  pickupStart: MaybeUndef<string>;
  pickupEnd: MaybeUndef<string>;
  expectedDeliveryTime: MaybeUndef<string>;
  deliveryStart: MaybeUndef<string>;
  deliveryEnd: MaybeUndef<string>;
  signedBy: string;
  equipmentName: string;
};

export type AdditionalReference = {
  qualifier: string;
  number: string;
  weight: number;
  pieces: number;
  shouldSendToDriver: boolean;
  qualifierId?: number; // Optional qualifierId for 3G reference number requirements
};

export type Pickup = LoadStop & {
  readyTime: MaybeUndef<string>;
  readyTimeEnd?: MaybeUndef<string>;
};

export type Dropoff = LoadStop & {
  mustDeliver: MaybeUndef<string>;
  mustDeliverEnd?: MaybeUndef<string>;
};

export type LoadStop = CompanyCoreInfo & {
  businessHours: string;
  refNumber: string;
  refNumberCandidates?: string[];
  apptRequired: Maybe<boolean>;
  /** From API (e.g. GlobalTranz); form displays as apptConfirmedWithLocation */
  apptConfirmed?: Maybe<boolean>;
  apptConfirmedWithLocation?: Maybe<boolean>; // Appointment Confirmed With Location (GlobalTranz)
  apptStartTime: MaybeUndef<string>;
  apptEndTime: MaybeUndef<string>;
  apptNum: string;
  apptNote: string;
  extractedPoNums?: string; // Only on the load object right after TransportPro pull, after save use stops[].poNumbers
  extractedPalletCount?: number; // Only on the load object right after TransportPro pull, after save use stops[].palletCount
  rescheduleReason?: string;
  timezone: string; // IANA timezone e.g. America/New_York
  /** Dock hours open/close (HH:MM) sent to BE for CreateOrder */
  openTime?: string;
  closeTime?: string;
  /** Per-stop reference numbers (e.g. McLeod PU/SO); mirrors BE Pickup/Consignee JSON */
  additionalReferences?: AdditionalReference[];
};

/**
 * A **shipment line** on a load: what `NormalizedLoad.commodities` holds and what create/update
 * load sends. Form-oriented (`weightTotal`, `dimensionUnit`, …), shared across TMS load-building UIs.
 * Not the same as `TMSCommodity` (customer catalog from commodity search APIs).
 */
export type Commodity = {
  ID?: number;
  CreatedAt?: string;
  UpdatedAt?: string;
  DeletedAt?: MaybeUndef<string>;
  externalTmsId?: string;
  matchedToTMSCommodity?: boolean;
  handlingQuantity: number;
  packagingType: string;
  length: number;
  width: number;
  height: number;
  dimensionUnit: string; // e.g. ft, cm
  weightTotal: number;
  weightUnit?: string; // Weight unit (lbs, kg) - for GlobalTranz
  hazardousMaterial: boolean;
  quantity: number;
  freightClass: string;
  /** Lookup id for freight class (e.g. GlobalTranz). */
  freightClassId?: number;
  nmfc: string;
  description: string;
  additionalMarkings: string;
  unNumber: string;
  packagingGroup: string;
  referenceNumber: string;
  hazmatCustomClassDescription: string;
  hazmatPieceDescription: string;
  harmonizedCode: string;
  hazardClasses: MaybeUndef<string[]>;
  loadID: number;
  // GlobalTranz HazMat fields
  hazmatClassId?: number;
  hazmatGroupId?: number;
  hazmatPrefixId?: number;
  hazmatCode?: string;
  hazmatChemicalName?: string;
  hazmatEmergencyContact?: string;
  // GlobalTranz Stackable
  stackable?: boolean;
  // Multi-stop (MGB): indices into form `stops[]` for pick and drop (0-based).
  pickupStopNumber?: number | null;
  dropStopNumber?: number | null;
  /** Stable MGB child-shipment Drumkit UUID ({loadUUID}-N); persisted by backend for updates. */
  shipmentDrumkitUUID?: string;
  // Turvo-specific fields
  totalPieces?: ValueUnit;
  grossWeight?: ValueUnit;
  netWeight?: ValueUnit;
  /** Turvo handling units label; must match backend Turvo item unit map (e.g. "Pallets", "Cases"). */
  handlingUnitTypeName?: string;
  /** Turvo item attributes.perishable */
  perishable?: boolean;
  /** Turvo item value.amount (USD) */
  value?: number;
  /** Turvo item minTemp.temp (°F) */
  itemsMinTemp?: number;
  /** Turvo item maxTemp.temp (°F) */
  itemsMaxTemp?: number;
};

export const createNewCommodity = (options?: {
  dimensionUnit?: string;
}): Commodity => ({
  ID: undefined,
  CreatedAt: undefined,
  UpdatedAt: undefined,
  DeletedAt: undefined,
  handlingQuantity: 0,
  packagingType: '',
  length: 0,
  width: 0,
  height: 0,
  dimensionUnit: options?.dimensionUnit ?? '', // Only set default for GlobalTranz
  weightTotal: 0,
  weightUnit: 'lbs', // Default to lbs
  hazardousMaterial: false,
  quantity: 0,
  freightClass: '',
  nmfc: '',
  description: '',
  additionalMarkings: '',
  unNumber: '',
  packagingGroup: '',
  referenceNumber: '',
  hazmatCustomClassDescription: '',
  hazmatPieceDescription: '',
  harmonizedCode: '',
  hazardClasses: undefined,
  loadID: 0,
  // GlobalTranz HazMat fields
  hazmatClassId: undefined,
  hazmatGroupId: undefined,
  hazmatPrefixId: undefined,
  hazmatCode: '',
  hazmatChemicalName: '',
  hazmatEmergencyContact: '',
  // GlobalTranz Stackable
  stackable: false,
  // Turvo-specific fields
  totalPieces: { val: 0, unit: null },
  grossWeight: { val: 0, unit: null },
  netWeight: { val: 0, unit: null },
  handlingUnitTypeName: '',
  perishable: false,
  value: undefined,
  itemsMinTemp: undefined,
  itemsMaxTemp: undefined,
});

export type ValueUnit = {
  val: number;
  unit: Maybe<Unit>;
};

export enum Unit {
  // Currency
  //---------------
  USD = 'USD',
  CAD = 'CAD',

  // Weight
  //---------------
  Kg = 'kg',
  Pounds = 'lbs',
  // Turvo specific
  Tonne = 't',
  Ounce = 'oz',
  Ton = 'ton',
  Gram = 'g',
  Pound = 'lb',

  // Pieces
  //---------------
  Boxes = 'boxes',
  Crates = 'crates',
  Pallets = 'pallets',
  // Turvo specific
  MetricTons = 'Metric tons',
  Bottles = 'Bottles',
  Packages = 'Packages',
  Racks = 'Racks',
  Bin = 'Bin',
  Piece = 'Piece',
  Ream = 'Ream',
  Spack = 'Spack',
  Spool = 'Spool',
  Bushels = 'Bushels',
  Containers = 'Containers',
  Gallons = 'Gallons',
  Tons = 'Tons',
  Kegs = 'Kegs',
  Cases = 'Cases',
  Truckload = 'Truckload',
  Blocks = 'Blocks',
  Bulk = 'Bulk',
  Barrels = 'Barrels',
  Units = 'Units',
  Each = 'Each',
  TurvoPallets = 'Pallets',
  BaseUnits = 'Base units',
  TurvoBoxes = 'Boxes',
  Loose = 'Loose',
  Bags = 'Bags',
  Bales = 'Bales',
  Bundles = 'Bundles',
  Cans = 'Cans',
  Carboys = 'Carboys',
  Carpets = 'Carpets',
  Cartons = 'Cartons',
  Coils = 'Coils',
  TurvoCrates = 'Crates',
  Cylinders = 'Cylinders',
  Drums = 'Drums',
  Pails = 'Pails',
  Reels = 'Reels',
  Lbs = 'Lbs',
  Rolls = 'Rolls',
  Kgs = 'Kgs',
  Skids = 'Skids',
  Liters = 'Liters',
  Totes = 'Totes',
  TubesPipes = 'Tubes/pipes',
  Vehicles = 'Vehicles',
  Other = 'Other',
  Kit = 'Kit',
  Pack = 'Pack',
  Pair = 'Pair',
  Feet = 'Feet',
  InnerPack = 'Inner Pack',
  Grams = 'Grams',
  Layer = 'Layer',
  Items = 'Items',
  Buckets = 'Buckets',
  Combinations = 'Combinations',
  HundredweightOnNet = 'Hundredweight on Net',
  Pouches = 'Pouches',
  Trays = 'Trays',
  Tubs = 'Tubs',

  // Distance
  //---------------
  Miles = 'miles',
  Km = 'km',
}

export type Specifications = {
  // Different from Mode which is the type of load (e.g. TL, LTL)
  // OrderType is primarily for McleodEnterprise and enums vary by tenant
  orderType: string;
  planningComment: string;
  /** McLeod loadboard posting Comment 1 (`pnn_comment`) */
  pnnComment?: string;
  /** McLeod loadboard posting Comment 2 (`pnn_comment2`) */
  pnnComment2?: string;
  totalInPalletCount: MaybeUndef<number>;
  totalOutPalletCount: MaybeUndef<number>;
  palletsRequired: boolean;
  totalPieces: Maybe<ValueUnit>;
  totalPiecesType: string;
  commodities: string;
  numCommodities: MaybeUndef<number>;
  transportType: string; // Van, Flatbed, Reefer, etc
  /** Builder suggestions only: how the backend chose transportType ("unknown" = email named none). */
  transportTypeSource?: string;
  // Builder suggestions only: true when nothing in the email or the customer's history settled
  // the equipment, so transportType is only the service's default. Omitted when false.
  transportTypeUnconfirmed?: boolean;
  hazmat?: boolean;
  oversized?: boolean;
  serviceType: string;
  shipmentType?: string; // Spot Quote, Contract Ongoing (for GlobalTranz TL/TL E-tender)
  service?: string; // Service option for GlobalTranz TL: Full ('1'), Partial ('2'), Volume ('4')
  canGet?: boolean; // Can Get checkbox for GlobalTranz TL
  transportSize: string;
  /** Dynamic equipment dimensions (L, W, H, etc.) keyed by display field id */
  equipmentDimensions?: Record<string, string>;
  totalWeight: Maybe<ValueUnit>;
  totalVolume: Maybe<ValueUnit>;
  netWeight: Maybe<ValueUnit>;
  billableWeight: Maybe<ValueUnit>;
  totalDistance: MaybeUndef<ValueUnit>;
  minTempFahrenheit: MaybeUndef<number>;
  maxTempFahrenheit: MaybeUndef<number>;
};

export type CommodityItem = {
  description: string;
  totalPieces: ValueUnit;
  grossWeight: ValueUnit;
  netWeight: ValueUnit;
};

export type CostLineItem = {
  label: string; // Required
  unitBasis: string; // e.g. flat, hour, mile, etc.
  quantity: number; // if unit basis is flat, then quantity is 1, otherwise quantity is the number of units (miles, hours, weight, etc)
  ratePerUnitUSD: number; // e.g. $100 / hour, $15 / mile, etc.
  totalChargeUSD: number; // i.e. Quantity * RatePerUnitUSD
  note: string; // optional
  // GlobalTranz accessorialId for rate/charge types (accessorialTypeId 0). Sent in CreateOrder cost/revenue lineItems.
  accessorialId?: number;
  // MGB service-type / EDI code from accessorial dropdown (e.g. LGD).
  accessorialCode?: string;
};

export type RateData = {
  salesperson1: string;
  salesperson1Percent: number;

  collectionMethod: string; // e.g. Prepaid, Collect, Third-Party, etc
  revenueCode: string; // Department/team in brokerage receiving the revenue
  customerRateType: string; // e.g. FlatRate, All In, Hourly, Mileage

  // Customer rate info
  customerLineHaulCharge: Maybe<ValueUnit>; // ValueUnit with currency (e.g. USD)
  customerRateNumUnits: Maybe<number>; // Number of units for rate type (e.g. miles, hours, etc.)
  customerLineHaulRate: Maybe<number>; // Per unit rate (e.g. $/mile or $/hour)
  customerLineHaulUnit: string; // Unit for the rate (e.g. miles, hours, etc.)
  customerTotalCharge: Maybe<ValueUnit>; // ValueUnit.Unit is currency (e.g. USD).
  customerLineItems: CostLineItem[]; // e.g. detention fee, drayage fee, etc

  // Carrier rate info
  carrierRateType: string; // e.g. FlatRate, All In, Hourly, Mileage
  carrierLineHaulCharge: Maybe<ValueUnit>; // ValueUnit with currency (e.g. USD)
  carrierRateNumUnits: Maybe<number>; // Number of units for rate type (e.g. miles, hours, etc.)
  carrierLineHaulRate: Maybe<number>; // Per unit rate (e.g. $/mile or $/hour)
  carrierLineHaulUnit: string; // Unit for the rate (e.g. miles, hours, etc.)
  carrierTotalCost: Maybe<ValueUnit>; // ValueUnit.Unit is currency (e.g. USD).
  carrierLineItems: CostLineItem[]; // e.g. detention fee, drayage fee, etc
  carrierMaxRate: Maybe<number>;

  fscPercent: Maybe<number>; // 0 - 100
  fscPerMile: Maybe<number>;
  fscFlatRate: Maybe<number>;

  netProfitUSD: Maybe<number>;
  profitPercent: Maybe<number>; // 0 - 100

  // GlobalTranz specific financial fields
  priceToBeat?: Maybe<number>; // Price to beat
  rate?: Maybe<number>; // Rate
  miles?: Maybe<number>; // Miles
  carrierCost?: Maybe<number>; // Target Cost
};

// For Aljex loads whose timestamps are timezone-agnostic, normalizes all timestamps to be in UTC,
// regardless of the user's locale
export type AccessorialItem = {
  id: number;
  displayText: string;
  value?: string | number | Record<number, string | number>; // For dependent fields - can be single value or object with displayField id as key
};

export type NormalizedLoad = {
  // Capitalized only because of underlying gorm.Model
  ID: MaybeUndef<number>;
  CreatedAt: string;
  UpdatedAt: string;
  DeletedAt: MaybeUndef<string>;

  serviceID: number;
  tmsID: number;
  freightTrackingID: string;
  drumkitUUID: string;
  isPlaceholder: boolean;
  externalTMSID: string;
  status: string;
  mode: string;
  moreThanTwoStops: boolean;
  poNums: MaybeUndef<string>;
  batchLoad?: boolean;
  operator: string;
  additionalReferences: AdditionalReference[];
  rateData: RateData;
  customer: Customer;
  billTo: CompanyCoreInfo;
  pickup: NormalizedPickup;
  consignee: NormalizedDropoff;
  carrier: NormalizedLoadCarrier;
  specifications: Specifications;
  notes: Note[];
  commodities: Commodity[];
  declaredValueUSD?: number; // Total shipment value in USD (for GlobalTranz cargoValue)
  accessorials?: AccessorialItem[];
  stops?: LoadStopsItem[];
  url?: string;
};

export type NormalizedLoadCarrier = {
  name: string;
  externalTMSID: string;
  mcNumber: string;
  dotNumber: string;
  phone: string;
  dispatcher: string;
  notes: string;
  sealNumber: string;
  scac: string;
  firstDriverName: string;
  firstDriverPhone: string;
  secondDriverName: string;
  secondDriverPhone: string;
  email: string;
  dispatchSource: CheckCallSource;
  dispatchCity: string;
  dispatchState: string;
  trackingMethod: string;
  truckNumber: string;
  trailerNumber: string;
  tractorVIN: string;
  rateConfirmationSent: boolean;
  confirmationSentTime: Maybe<Date>;
  confirmationReceivedTime: Maybe<Date>;
  dispatchedTime: Maybe<Date>;
  expectedPickupTime: Maybe<Date>;
  pickupStart: Maybe<Date>;
  pickupEnd: Maybe<Date>;
  expectedDeliveryTime: Maybe<Date>;
  deliveryStart: Maybe<Date>;
  deliveryEnd: Maybe<Date>;
  signedBy: string;
  equipmentName: string;
};

export type NormalizedPickup = NormalizedLoadStop & {
  // Capitalized only because of underlying gorm.Model
  ID: MaybeUndef<number>;
  apptConfirmed: Maybe<boolean>; // happens in load information tab
  readyTime: Maybe<LoadDateValue>;
  readyTimeEnd: Maybe<Date>; // Planned end (MGB); other TMSes typically leave null.
};

export type NormalizedDropoff = NormalizedLoadStop & {
  // Capitalized only because of underlying gorm.Model
  ID: MaybeUndef<number>;
  apptConfirmed: Maybe<boolean>; // happens in load information tab
  mustDeliver: Maybe<LoadDateValue>;
  mustDeliverEnd: Maybe<Date>; // Planned end (MGB); other TMSes typically leave null.
};

export type NormalizedLoadStop = CompanyCoreInfo & {
  businessHours: string;
  refNumber: string;
  refNumberCandidates?: string[];
  additionalReferences: AdditionalReference[];
  apptRequired: Maybe<boolean>;
  apptConfirmedWithLocation?: Maybe<boolean>; // Appointment Confirmed With Location (GlobalTranz)
  apptStartTime: Maybe<Date>;
  apptEndTime: Maybe<Date>;
  apptType: Maybe<string>;
  apptNum: string;
  apptNote: string;
  extractedPoNums?: string; // Only on the load object right after TransportPro pull, after save use stops[].poNumbers
  extractedPalletCount?: number; // Only on the load object right after TransportPro pull, after save use stops[].palletCount
  rescheduleReason?: string;
  timezone: string; // IANA timezone e.g. America/New_York
  instructions: string;
  referenceNumber: string;
  locationType: string;
  /** HH:MM string (e.g. 3G dock hours) or Date depending on TMS/form path */
  openTime: Maybe<Date | string>;
  closeTime: Maybe<Date | string>;
  tsaCompliant: Maybe<boolean>;
};

/** Normalize time string to HH:MM with two-digit hour so Select value matches options (e.g. "7:00" -> "07:00"). */
function normalizeHHMM(value: unknown): string {
  if (value == null || value === '') return '';
  const s = String(value).trim();
  const match = s.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return s;
  const [, h, m] = match;
  return `${(h ?? '').padStart(2, '0')}:${(m ?? '').padStart(2, '0')}`;
}

/** Map API accessorials (externalId, name) to form shape (id, displayText) so checkboxes tick correctly. */
function normalizeAccessorialsForForm(
  tmsName: string,
  accessorials: unknown
): AccessorialItem[] {
  const arr =
    normalizeToArray(
      accessorials as Undef<
        | Record<string, unknown>[]
        | Record<number | string, Record<string, unknown>>
      >
    ) ?? [];
  const isMercurygateBoomi = tmsName === TMS.MercurygateBoomi;

  return arr.map((acc: Record<string, unknown>) => ({
    // Prefer externalId (API/DB accessorial type ID); fall back to id when externalId is missing (e.g. form state).
    id: (acc.externalId ?? acc.id) as number,
    displayText: (acc.displayText ?? acc.name ?? '') as string,
    value: (acc.value ??
      (isMercurygateBoomi ? acc.code : undefined)) as AccessorialItem['value'],
  }));
}

/**
 * Normalize API commodities to form shape so shipment items display when loading a load from TMS.
 * GlobalTranz and TransportPro may send dimensionsUnit; form expects dimensionUnit.
 * Other TMSs unchanged.
 */
function normalizeCommoditiesForForm(
  tmsName: string,
  commodities: unknown[]
): Commodity[] {
  const isGlobalTranz = tmsName === TMS.GlobalTranzTMS;
  const isTransportPro = tmsName === TMS.TransportPro;
  const isTurvo = tmsName === TMS.Turvo;
  return (commodities || []).map((c: unknown) => {
    const row = (c ?? {}) as Record<string, unknown>;
    // Only GlobalTranz: API (BE models.Commodity) may send dimensionsUnit; form uses dimensionUnit.
    // Backend now normalizes dimUnitId:
    // - 0 -> "inches"
    // - 1 -> "feet"
    // For backward-compatibility with older saved values ("imperial"/"metric"),
    // coerce them into the expected strings as well.
    const dimensionUnitFallback =
      isGlobalTranz || isTransportPro ? (row.dimensionsUnit as string) : '';
    let rawDimensionUnit =
      (row.dimensionUnit as string) ?? dimensionUnitFallback ?? '';
    if (isGlobalTranz) {
      const lower = rawDimensionUnit.toLowerCase();
      if (!lower) {
        rawDimensionUnit = 'inches';
      } else if (lower === 'imperial') {
        rawDimensionUnit = 'inches';
      } else if (lower === 'metric') {
        rawDimensionUnit = 'feet';
      }
    }
    const dimensionUnit = isGlobalTranz
      ? rawDimensionUnit || 'inches'
      : rawDimensionUnit;

    const handlingUnitTypeName = isTurvo
      ? normalizeTurvoItemUnitForForm(row.handlingUnitTypeName as string)
      : ((row.handlingUnitTypeName as string) ?? undefined);

    return {
      ...row,
      dimensionUnit,
      ...(isTurvo ? { handlingUnitTypeName } : {}),
    } as Commodity;
  });
}

export function normalizeLoad(tmsName: string, load: Load) {
  // For Revenova TMS, map commodities array to specifications
  const isRevenovaTMS = tmsName === TMS.Revenova;
  const firstCommodity = load.commodities?.[0];

  const rawTransportType = load.specifications?.transportType;

  let transportTypeForForm: string;
  if (
    tmsName === TMS.GlobalTranzTMS ||
    tmsName === TMS.MercurygateBoomi ||
    tmsName === TMS.PLS
  ) {
    transportTypeForForm = rawTransportType ?? '';
  } else if (tmsName === TMS.Turvo) {
    transportTypeForForm =
      normalizeTurvoTransportTypeForForm(rawTransportType) || 'Van';
  } else {
    transportTypeForForm = rawTransportType || 'Truckload';
  }

  let transportSizeForForm: Undef<string>;
  if (tmsName === TMS.Turvo) {
    transportSizeForForm =
      normalizeTurvoTransportSizeForForm(load.specifications?.transportSize) ||
      '';
  }

  return {
    ...load,
    mode: load.mode || 'TL', // Use API value (TL, LTL, Refrigerated) or default to 'TL'
    poNums: load.poNums,
    batchLoad: load.batchLoad ?? false,
    customer: normalizeDatesForTMSForm(tmsName, load.customer),
    billTo: normalizeDatesForTMSForm(tmsName, load.billTo),
    pickup: {
      ...load.pickup,
      ...normalizeDatesForTMSForm(tmsName, load.pickup),
      timezone: normalizeTimezone(load.pickup?.timezone) || '',
      openTime: normalizeHHMM(load.pickup?.openTime),
      closeTime: normalizeHHMM(load.pickup?.closeTime),
      // API sends apptConfirmed; form uses apptConfirmedWithLocation (Appointment Confirmed With Location)
      apptConfirmedWithLocation:
        load.pickup?.apptConfirmed ??
        load.pickup?.apptConfirmedWithLocation ??
        null,
      apptNum: load.pickup?.apptNum ?? '',
      apptNote: load.pickup?.apptNote ?? '',
    },
    consignee: {
      ...load.consignee,
      ...normalizeDatesForTMSForm(tmsName, load.consignee),
      timezone: normalizeTimezone(load.consignee?.timezone) || '',
      openTime: normalizeHHMM(load.consignee?.openTime),
      closeTime: normalizeHHMM(load.consignee?.closeTime),
      // API sends apptConfirmed; form uses apptConfirmedWithLocation (Appointment Confirmed With Location)
      apptConfirmedWithLocation:
        load.consignee?.apptConfirmed ??
        load.consignee?.apptConfirmedWithLocation ??
        null,
      apptNum: load.consignee?.apptNum ?? '',
      apptNote: load.consignee?.apptNote ?? '',
    },
    carrier: normalizeDatesForTMSForm(tmsName, load.carrier),
    specifications: {
      ...load.specifications,
      equipmentDimensions: load.specifications?.equipmentDimensions ?? {},
      // GlobalTranz: use as-is (e.g. TOFC, Van). Turvo: map API enums to form Select labels.
      transportType: transportTypeForForm,
      ...(transportSizeForForm !== undefined
        ? { transportSize: transportSizeForForm }
        : {}),
      // Map commodities data to specifications for Revenova
      ...(isRevenovaTMS &&
        firstCommodity && {
          commodities:
            firstCommodity.description || load.specifications.commodities || '',
          totalPieces: {
            val:
              firstCommodity.handlingQuantity ||
              load.specifications.totalPieces?.val ||
              0,
            unit:
              firstCommodity.packagingType ||
              load.specifications.totalPieces?.unit ||
              '',
          },
        }),
    },
    stops: (normalizeToArray(load.stops) || []).map((s) => {
      const normalizedStopDates = normalizeDatesForTMSForm(tmsName, {
        readyTime: s.readyTime,
        readyEndTime: s.readyEndTime,
        mustDeliver: s.mustDeliver,
        apptStartTime: s.apptStartTime,
        apptEndTime: s.apptEndTime,
        actualStartTime: s.actualStartTime,
        actualEndTime: s.actualEndTime,
        expectedStartTime: s.expectedStartTime,
      });

      return {
        ...s,
        ...normalizedStopDates,
      };
    }),
    rateData: {
      ...load.rateData,
      customerLineItems: normalizeToArray(load.rateData.customerLineItems),
      carrierLineItems: normalizeToArray(load.rateData.carrierLineItems),
    },
    additionalReferences: normalizeToArray(load.additionalReferences) || [],
    commodities: normalizeCommoditiesForForm(
      tmsName,
      normalizeToArray(load.commodities) || []
    ),
    accessorials: normalizeAccessorialsForForm(
      tmsName,
      (load as any).accessorials
    ),
    notes: normalizeToArray(load.notes) || [],
  } as unknown as NormalizedLoad;
}

export function mapUnit(unitStr: string): Maybe<Unit> {
  switch (unitStr.toLowerCase()) {
    case 'kg':
      return Unit.Kg;
    case 'lbs':
      return Unit.Pounds;
    case 'boxes':
      return Unit.Boxes;
    case 'miles':
      return Unit.Miles;
    case 'km':
      return Unit.Km;
    case 'pallets':
      return Unit.Pallets;
    case 'crates':
      return Unit.Crates;
    default:
      return null;
  }
}

export type TMSCustomer = CompanyCoreInfo & {
  // Numeric DB id from backend (gorm.Model). Used e.g. for LTL commodities API customerID.
  id?: number;
  ID?: number;
  refNumber: string;
  ediEnabled?: boolean;
  defaultCommodity?: string;
  /** 3G-only: when true, compute implied freight class from dimensions/weight and filter commodities. */
  enableAutoFreightClass?: boolean;
  isTest?: boolean;
};

export type TMSLocation = CompanyCoreInfo & {
  // Capitalized only because of underlying gorm.Model
  ID: MaybeUndef<number>;
  apptRequired: Maybe<boolean>;
  isShipper?: boolean;
  isConsignee?: boolean;
  driverLoadingResponsibility?: string; // e.g. No (un)loading, driver load, drop & hook trailer. Primarily for Mcleod
  carrier?: TMSCarrier;
  notes?: string;
  instructions?: string;
  note?: string;
  refNumber?: string;
  locationType?: string;
  /** HH:MM (e.g. 3G) or Date; matches NormalizedLoadStop for injectSelectedObject compatibility */
  openTime?: Maybe<Date | string>;
  closeTime?: Maybe<Date | string>;
  defaultGroupId?: number; // GlobalTranz specific: default group ID for location
  isTSACompliant?: boolean;
  isPickupAptRequired?: boolean;
  isDeliveryAptRequired?: boolean;
  /** 3G TMS numeric loc id; used to fetch default contact from GET /locations/:locID */
  threeGLocationId?: Maybe<number>;
  accessorials?: string[];
};

export type TMSLocationWithDistance = TMSLocation & {
  milesDistance?: number;
  emails?: Maybe<string[]>;
};

export type TMSCarrier = CompanyCoreInfo & {
  // Capitalized only because of underlying gorm.Model
  ID: MaybeUndef<number>;
  dotNumber: string;
};

/**
 * A row from the **TMS commodity catalog** (`getCommodities` / `getCommodityDetails`): saved
 * product defaults for a customer. Used to prefill `Commodity` shipment lines; field names match
 * those APIs (`weightAmount`, `commodityDescription`, …), not the load `Commodity` shape.
 */
export type TMSCommodity = {
  // Capitalized only because of underlying gorm.Model
  ID: MaybeUndef<number>;
  externalTMSID: string;
  tmsIntegrationId?: number;
  commodity?: string;
  commodityDescription?: string;
  productDefaultDetailsId?: number;
  weightAmount?: Maybe<number>;
  weightUnit?: Maybe<string>;
  weightUnitId?: Maybe<number>;
  length?: Maybe<number>;
  width?: Maybe<number>;
  height?: Maybe<number>;
  dimUnit?: Maybe<string>;
  dimUnitId?: Maybe<number>;
  handlingUnitTypeId?: Maybe<number>;
  handlingUnitCount?: Maybe<number>;
  pieceCount?: number;
  freightClassId?: Maybe<number>;
  freightClass?: string;
  nmfc?: string;
  nmfcNumber?: string;
  hazardousMaterial?: boolean;
  hazmatClass?: Maybe<string>;
  hazmatClassId?: number;
  hazmatGroup?: Maybe<string>;
  hazmatGroupId?: Maybe<number>;
  hazmatCode?: Maybe<string>;
  hazmatPrefixId?: number;
  hazmatChemicalName?: Maybe<string>;
  hazmatEmergencyContactNumber?: Maybe<string>;
  stackable?: boolean;
  carrierType?: number;
  density?: Maybe<number>;
  hasDetailsFetched?: boolean;
  productDetailsJson?: Maybe<string>;
};

export function createInitCompanyCoreInfo(): CompanyCoreInfo {
  return {
    externalTMSID: '',
    name: '',
    nameAddress: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    contact: '',
    phone: '',
    email: '',
  };
}

// Use factory functions to avoid mutating const object (alternative is using Object.freeze(),
// but this made intent more explicit)
export function createInitRateData(): RateData {
  return {
    salesperson1: '',
    salesperson1Percent: 0,

    revenueCode: '',
    collectionMethod: '',

    customerRateType: '',
    customerLineHaulCharge: null,
    customerRateNumUnits: null,
    customerLineHaulRate: null,
    customerLineHaulUnit: '',
    customerTotalCharge: null,

    fscPercent: null,
    fscPerMile: null,
    fscFlatRate: null,

    customerLineItems: [],

    carrierRateType: '',
    carrierLineHaulCharge: null,
    carrierRateNumUnits: null,
    carrierLineHaulRate: null,
    carrierLineHaulUnit: '',
    carrierTotalCost: null,
    carrierLineItems: [],

    carrierMaxRate: null,

    // GlobalTranz financial fields — must be explicit nulls so empty number
    // inputs are not treated as dirty vs missing defaultValues keys.
    priceToBeat: null,
    rate: null,
    miles: null,
    carrierCost: null,

    netProfitUSD: null,
    profitPercent: null,
  };
}

export function createInitSpecs(): Specifications {
  return {
    orderType: '',
    planningComment: '',
    pnnComment: '',
    pnnComment2: '',
    totalInPalletCount: 0,
    totalOutPalletCount: 0,
    palletsRequired: false,
    totalPieces: null,
    totalPiecesType: '',
    commodities: '',
    numCommodities: 0,
    totalWeight: null,
    totalVolume: null,
    billableWeight: null,
    netWeight: null,
    totalDistance: null,
    transportType: '',
    hazmat: false,
    serviceType: '',
    transportSize: '',
    equipmentDimensions: {},
    minTempFahrenheit: null,
    maxTempFahrenheit: null,
  };
}

export function createInitLoad(): Load {
  return {
    ID: null,
    CreatedAt: '',
    UpdatedAt: '',
    DeletedAt: null,
    serviceID: 0,
    tmsID: 0,
    freightTrackingID: '',
    drumkitUUID: '',
    isPlaceholder: false,
    externalTMSID: '',
    status: '',
    mode: '',
    moreThanTwoStops: false,
    poNums: '',
    batchLoad: false,
    operator: '',
    additionalReferences: [],
    rateData: {
      salesperson1: '',
      salesperson1Percent: 0,

      revenueCode: '',
      collectionMethod: '',

      customerRateType: '',
      customerLineHaulCharge: null,
      customerRateNumUnits: null,
      customerLineHaulRate: null,
      customerLineHaulUnit: '',
      customerTotalCharge: null,
      customerLineItems: [],

      fscPercent: null,
      fscPerMile: null,
      fscFlatRate: null,
      carrierRateType: '',
      carrierLineHaulCharge: null,
      carrierRateNumUnits: null,
      carrierLineHaulRate: null,
      carrierLineHaulUnit: '',
      carrierTotalCost: null,
      carrierLineItems: [],

      carrierMaxRate: null,

      // GlobalTranz financial fields — keep in sync with createInitRateData()
      priceToBeat: null,
      rate: null,
      miles: null,
      carrierCost: null,

      netProfitUSD: null,
      profitPercent: null,
    },
    customer: {
      externalTMSID: '',
      name: '',
      nameAddress: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      zipCode: '',
      country: '',
      contact: '',
      phone: '',
      email: '',
      refNumber: '',
    },
    billTo: {
      externalTMSID: '',
      name: '',
      nameAddress: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      zipCode: '',
      contact: '',
      phone: '',
      email: '',
    },
    stops: [
      {
        id: '',
        order: 0,
        loadID: 0,
        stopType: LoadStopsItemTypes.Pickup,
        stopNumber: 0,
        externalTMSID: '',
        address: {
          name: '',
          addressLine1: '',
          addressLine2: '',
          city: '',
          state: '',
          zip: '',
          timezone: '',
          country: '',
          isVerified: false,
        },
        contact: '',
        phone: '',
        email: '',
        businessHours: '',
        refNumber: '',
        readyTime: '',
        readyEndTime: '',
        mustDeliver: '',
        apptRequired: false,
        apptType: '',
        apptStartTime: '',
        apptEndTime: '',
        actualStartTime: '',
        actualEndTime: '',
        expectedStartTime: '',
        apptNum: '',
        apptNote: '',
        timezone: '',
        additionalReferences: [],
      },
      {
        id: '',
        order: 1,
        loadID: 0,
        stopType: LoadStopsItemTypes.Dropoff,
        stopNumber: 1,
        externalTMSID: '',
        address: {
          name: '',
          addressLine1: '',
          addressLine2: '',
          city: '',
          state: '',
          zip: '',
          timezone: '',
          country: '',
          isVerified: false,
        },
        contact: '',
        phone: '',
        email: '',
        businessHours: '',
        refNumber: '',
        readyTime: '',
        readyEndTime: '',
        mustDeliver: '',
        apptRequired: false,
        apptType: '',
        apptStartTime: '',
        apptEndTime: '',
        actualStartTime: '',
        actualEndTime: '',
        expectedStartTime: '',
        apptNum: '',
        apptNote: '',
        timezone: '',
        additionalReferences: [],
      },
    ],
    pickup: {
      externalTMSID: '',
      name: '',
      nameAddress: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      zipCode: '',
      country: '',
      contact: '',
      phone: '',
      email: '',
      businessHours: '',
      refNumber: '',
      readyTime: null,
      readyTimeEnd: null,
      apptRequired: null,
      apptConfirmedWithLocation: null,
      apptStartTime: null,
      apptEndTime: null,
      apptNum: '',
      apptNote: '',
      timezone: '',
      additionalReferences: [],
    },
    consignee: {
      externalTMSID: '',
      name: '',
      nameAddress: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      zipCode: '',
      country: '',
      contact: '',
      phone: '',
      email: '',
      businessHours: '',
      refNumber: '',
      apptNum: '',
      mustDeliver: null,
      mustDeliverEnd: null,
      apptRequired: null,
      apptConfirmedWithLocation: null,
      apptStartTime: null,
      apptEndTime: null,
      apptNote: '',
      timezone: '',
      additionalReferences: [],
    },
    carrier: {
      externalTMSID: '',
      mcNumber: '',
      dotNumber: '',
      name: '',
      phone: '',
      dispatcher: '',
      notes: '',
      sealNumber: '',
      scac: '',
      firstDriverName: '',
      firstDriverPhone: '',
      secondDriverName: '',
      secondDriverPhone: '',
      email: '',
      dispatchSource: CheckCallSource.Dispatcher,
      dispatchCity: '',
      dispatchState: '',
      truckNumber: '',
      trailerNumber: '',
      rateConfirmationSent: false,
      confirmationSentTime: null,
      confirmationReceivedTime: null,
      dispatchedTime: null,
      expectedPickupTime: null,
      pickupStart: null,
      pickupEnd: null,
      expectedDeliveryTime: null,
      deliveryStart: null,
      deliveryEnd: null,
      signedBy: '',
      equipmentName: '',
    },
    specifications: {
      orderType: '',
      planningComment: '',
      pnnComment: '',
      pnnComment2: '',
      totalInPalletCount: 0,
      totalOutPalletCount: 0,
      palletsRequired: false,
      totalPieces: null,
      totalPiecesType: '',
      commodities: '',
      numCommodities: 0,
      totalWeight: null,
      totalVolume: null,
      billableWeight: null,
      netWeight: null,
      totalDistance: null,
      transportType: '',
      hazmat: false,
      serviceType: '',
      transportSize: '',
      equipmentDimensions: {},
      minTempFahrenheit: 0,
      maxTempFahrenheit: 0,
    },
    notes: [],
    commodities: [],
  };
}
